import React, { FC, Fragment, ReactNode, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import ButtonClose from "shared/ButtonClose/ButtonClose";

export interface NcModalProps {
  renderContent: () => ReactNode;
  renderTrigger?: (openModal: () => void) => ReactNode;
  contentExtraClass?: string;
  contentPaddingClass?: string;
  triggerText?: ReactNode;
  modalTitle?: ReactNode;
  isOpenProp?: boolean;
  svgIcon?: "pencil" | "plus";
  onCloseModal?: () => void;
  onOpenModal?: () => void;
}

const NcModal: FC<NcModalProps> = ({
  renderTrigger,
  renderContent,
  contentExtraClass = "max-w-md",
  contentPaddingClass = "p-6 md:p-8",
  modalTitle = "Modal title",
  isOpenProp,
  onCloseModal,
  onOpenModal,
}) => {
  const isControlled = typeof isOpenProp === "boolean";
  const [isOpen, setIsOpen] = useState(!!isOpenProp);

  useEffect(() => {
    if (isControlled) setIsOpen(!!isOpenProp);
  }, [isOpenProp]);

  const openModal = () => {
    if (!isControlled) setIsOpen(true);
    onOpenModal?.();
  };

  const closeModal = () => {
    if (!isControlled) setIsOpen(false);
    onCloseModal?.();
  };

  return (
    <div className="nc-NcModal">
      {!isControlled && renderTrigger && renderTrigger(openModal)}

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          onClose={closeModal}
        >
          <div className="min-h-screen px-4 text-center md:px-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm transition-opacity" />
            </Transition.Child>

            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95 translate-y-4"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-4"
            >
              <div
                className={`relative inline-block w-full ${contentExtraClass} my-8 overflow-hidden text-left align-middle transform bg-white dark:bg-neutral-900 rounded-2xl shadow-xl ring-1 ring-black/10 dark:ring-white/10 transition-all duration-300`}
              >
                {/* Close Button */}
                <div
                  className="absolute top-3 right-3 z-10 rounded-full p-1 bg-white bg-opacity-10 backdrop-blur-md text-white hover:text-indigo-300 transition"
                >
                  <ButtonClose
                    onClick={closeModal} />
                </div>

                {/* Modal Header */}
                {modalTitle && (
                  <div className="px-6 pt-6 pb-2 text-center border-b border-neutral-200 dark:border-neutral-700">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold text-neutral-800 dark:text-neutral-100"
                    >
                      {modalTitle}
                    </Dialog.Title>
                  </div>
                )}

                {/* Modal Content */}
                <div
                  className={`relative z-0 bg-white dark:bg-neutral-900 ${contentPaddingClass}`}
                >
                  {renderContent()}
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default NcModal;
